import {useState} from 'react';

import type * as RunFeed from '../../../util/runfeed';
import * as TableCols from '../../../util/tablecols';

type UseTableEditorControllerOptions = {
  config: RunFeed.Config;
  update(config: RunFeed.Config): void;
  hiddenColumns: string[];
  visibleColumns: string[];
};

type UseTableEditorControllerHook = {
  draggingColumnName: string | null;
  onDragStart(columnAccessor: string): void;
  onDropField(dropColumn: string): void;
  onContainerDrop(visible: boolean, pinned: boolean): void;
  onShowColumn(columnAccessor: string): void;
  onHideColumn(columnAccessor: string): void;
  onShowAll(): void;
  onHideAllUnpinned(): void;
  onTogglePinned(column: string): void;
};

/**
 * Manages drag-drop behavior for the table column editor. Given an input
 * run config, it provides functions to handle dragging and moving items from
 * one column to the other, as well as showing and hiding batches of columns.
 */
export function useTableEditorDragDropController({
  config,
  update,
  hiddenColumns,
  visibleColumns,
}: UseTableEditorControllerOptions): UseTableEditorControllerHook {
  const [draggingColumnName, setDraggingColumnName] = useState<string | null>(
    null
  );

  const onDragStart = (columnAccessor: string) => {
    setDraggingColumnName(columnAccessor);
  };

  const updateConfig: typeof update = conf => {
    update({...conf, columnConfigModified: true});
  };

  const onDropField = (dropColumn: string) => {
    let conf = config;
    if (draggingColumnName) {
      if (config.columnVisible[dropColumn]) {
        conf = TableCols.showColumns(conf, [draggingColumnName]);
        conf = TableCols.moveBefore(conf, draggingColumnName, dropColumn);
      } else {
        conf = TableCols.hideColumns(conf, [draggingColumnName]);
      }
      setDraggingColumnName(null);
    }
    updateConfig(conf);
  };

  const onContainerDrop = (visible: boolean, pinned: boolean) => {
    let conf = config;
    if (draggingColumnName) {
      if (visible) {
        conf = TableCols.showColumns(conf, [draggingColumnName]);
        conf = TableCols.moveToStart(conf, draggingColumnName);
        if (pinned) {
          conf = TableCols.togglePinned(conf, draggingColumnName);
        }
      } else {
        conf = TableCols.hideColumns(conf, [draggingColumnName]);
      }
      setDraggingColumnName(null);
    }
    updateConfig(conf);
  };

  const onShowColumn = (columnAccessor: string) => {
    updateConfig(TableCols.showColumns(config, [columnAccessor], true));
  };

  const onHideColumn = (columnAccessor: string) => {
    updateConfig(TableCols.hideColumns(config, [columnAccessor]));
  };

  const onShowAll = () => {
    updateConfig(TableCols.showColumns(config, hiddenColumns, true));
  };

  const onHideAllUnpinned = () => {
    updateConfig(
      TableCols.hideColumns(
        config,
        visibleColumns.filter(c => !config.columnPinned[c])
      )
    );
  };

  const onTogglePinned = (column: string) => {
    updateConfig(TableCols.togglePinned(config, column));
  };

  return {
    draggingColumnName,
    onDragStart,
    onDropField,
    onContainerDrop,
    onShowColumn,
    onHideColumn,
    onShowAll,
    onHideAllUnpinned,
    onTogglePinned,
  };
}
